/**
 * Thời tiết M3: nắng nóng, mưa, gió biển (master-plan 3.4 — không sự kiện theo lịch).
 *
 * Mỗi khu có thời tiết riêng, xét đổi mỗi M16 giờ game: giữ nguyên với xác
 * suất M17, ngược lại chọn ngẫu nhiên loại khác áp dụng được cho khu.
 * Ảnh hưởng: lưu lượng khách (bình quân gia quyền theo lưu lượng chi nhánh)
 * và nhu cầu theo nhóm hàng (trọng số khi khách chọn nhóm mua).
 */
import { layThamSoMoPhong } from './du-lieu.js';
import { chiNhanhDaMo, luuLuongKhuTheoCap } from './khu-vuc.js';
import type { HamRng } from './rng.js';
import type { DuLieuGame, ThoiTiet, TrangThaiGame } from './types.js';

/** Các loại thời tiết áp dụng được cho một khu. */
export function thoiTietChoKhu(duLieu: DuLieuGame, maKhu: string): ThoiTiet[] {
  const ds = duLieu.thoiTiet.thoiTiet.filter((t) => t.khuApDung === 'all' || t.khuApDung === maKhu);
  if (ds.length === 0) throw new Error(`Không có thời tiết nào cho khu ${maKhu}.`);
  return ds;
}

/** Loại thời tiết hiện tại của một khu. */
export function thoiTietHienTaiCuaKhu(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  maKhu: string,
): ThoiTiet {
  const hienTai = state.thoiTiet[maKhu]?.loai;
  const tim = duLieu.thoiTiet.thoiTiet.find((t) => t.ma === hienTai);
  if (tim) return tim;
  return thoiTietChoKhu(duLieu, maKhu)[0]!;
}

/**
 * Cập nhật thời tiết mỗi tick (đếm ngược nội tại). Gọi 1 lần mỗi giờ game.
 * Thuần: chỉ dùng rng được inject.
 */
export function capNhatThoiTiet(state: TrangThaiGame, duLieu: DuLieuGame, rng: HamRng): void {
  const chuKy = layThamSoMoPhong(duLieu, 'M16');
  const giuNguyen = layThamSoMoPhong(duLieu, 'M17');
  for (const khu of duLieu.khuVuc.khuVuc) {
    const hienTai = state.thoiTiet[khu.ma] ?? {
      loai: thoiTietChoKhu(duLieu, khu.ma)[0]!.ma,
      demNguoc: chuKy,
    };
    hienTai.demNguoc -= 1;
    if (hienTai.demNguoc <= 0) {
      hienTai.demNguoc = chuKy;
      if (rng() >= giuNguyen) {
        const cacLoai = thoiTietChoKhu(duLieu, khu.ma).filter((t) => t.ma !== hienTai.loai);
        if (cacLoai.length > 0) {
          hienTai.loai = cacLoai[Math.floor(rng() * cacLoai.length)]!.ma;
        }
      }
    }
    state.thoiTiet[khu.ma] = hienTai;
  }
}

/**
 * Hệ số lưu lượng do thời tiết: bình quân gia quyền theo lưu lượng các chi
 * nhánh đang mở (khu chưa có chi nhánh không ảnh hưởng kinh tế).
 */
export function heSoThoiTietLuuLuong(state: TrangThaiGame, duLieu: DuLieuGame): number {
  let tu = 0;
  let mau = 0;
  for (const khu of duLieu.khuVuc.khuVuc) {
    if (!chiNhanhDaMo(state, khu)) continue;
    const phan = luuLuongKhuTheoCap(state, khu, duLieu);
    tu += phan * thoiTietHienTaiCuaKhu(state, duLieu, khu.ma).heSoLuuLuong;
    mau += phan;
  }
  return mau > 0 ? tu / mau : 1;
}

/** Hệ số nhu cầu một nhóm hàng do thời tiết (bình quân gia quyền như trên). */
export function heSoThoiTietNhom(state: TrangThaiGame, duLieu: DuLieuGame, nhom: string): number {
  let tu = 0;
  let mau = 0;
  for (const khu of duLieu.khuVuc.khuVuc) {
    if (!chiNhanhDaMo(state, khu)) continue;
    const phan = luuLuongKhuTheoCap(state, khu, duLieu);
    tu += phan * (thoiTietHienTaiCuaKhu(state, duLieu, khu.ma).heSoNhom[nhom] ?? 1);
    mau += phan;
  }
  return mau > 0 ? tu / mau : 1;
}

/** Hệ số cà phê do thời tiết ở Bãi biển (quầy cà phê đặt ở Bãi biển). */
export function heSoThoiTietCaPhe(state: TrangThaiGame, duLieu: DuLieuGame): number {
  return thoiTietHienTaiCuaKhu(state, duLieu, 'bai-bien').heSoCaPhe;
}
